import React, { useCallback, useEffect, useState } from 'react';
import { Card, Spinner, Alert, Btn, Input, Select, Modal } from '../../components/UI';
import { api } from '../../utils/api';

// W5 — Design in the customer workspace (HB Sales Tool visual contract, Design page).
// Starts from the standard composition of the project's programme (or empty days for a pending one)
// and keeps the existing day grid: 1 · Shape, 2 · Adapt (KEEP / MODIFY / DROP, + Unit, + Gap → new Unit),
// the level ladder, Pair with Measure, What changes for the client, the outline, and Gate 2.

const LADDER = [
  ['Standard', 'the programme as composed'],
  ['Shaped', 'days, rhythm and format chosen'],
  ['Adapted', 'Units kept, modified, dropped or added'],
  ['New', 'a gap filled with a new Unit'],
];
const RHYTHMS = ['3–4 weeks between modules', '2 weeks between modules', 'one module a month', 'consecutive days'];
const FORMATS = ['in person, full days', 'in person + virtual half-days', 'virtual half-days'];
const MEASURES = ['no measure', 'HB Compass — before and after', 'HB Compass — before only', 'Insights Discovery'];
const FAMILIES = { IE: 'Influence and Effectiveness', ML: 'Management and Leadership', SE: 'Sales Excellence' };
const ACTS = [['keep', 'KEEP'], ['modify', 'MODIFY'], ['drop', 'DROP']];
const GATE_RED = 'var(--gate)';

const tiny = { fontSize: '0.78rem', color: 'var(--muted)' };
const capsLabel = { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)' };
const h2 = { fontFamily: 'var(--font-body)', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', margin: 0 };
const panel = { padding: '20px 24px', marginBottom: 14 };

// Two columns (grid + side) on a wide screen, one below 980px — as in the contract.
function useWide() {
  const q = '(min-width: 980px)';
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q); const on = () => setWide(m.matches);
    m.addEventListener('change', on); return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>;
}

// The ladder: the level follows from what the trainer changes.
function Ladder({ level }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 8 }}>
      {LADDER.map(([name, sub], i) => (
        <div key={name} style={{
          flex: 1, minWidth: 130, padding: '8px 10px', fontSize: '0.8rem',
          border: `1px solid ${i === level ? 'var(--ink)' : 'var(--line)'}`,
          background: i === level ? 'var(--ink)' : i < level ? '#efefef' : '#fafafa',
          color: i === level ? '#fff' : 'var(--body-ink)',
        }}>
          <b style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '0.9rem' }}>{i + 1} · {name}</b>{sub}
        </div>
      ))}
    </div>
  );
}

// Starting point when the role track has no programme yet.
export function DesignStart({ trackId, data, onStarted, children }) {
  const opts = data.starts.options || [];
  const [slug, setSlug] = useState(data.starts.projectSlug && opts.some(o => o.slug === data.starts.projectSlug) ? data.starts.projectSlug : (opts[0]?.slug || ''));
  const [busy, setBusy] = useState(false);
  async function start() {
    setBusy(true);
    try { await api.master.startDesign(trackId, slug); onStarted(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <Card style={panel}>
      <h3 style={h2}>Start a design</h3>
      <p style={{ fontSize: '0.86rem', color: 'var(--body-ink)', lineHeight: 1.5, margin: '8px 0 14px' }}>
        Every design starts from a standard Hansen Beck composition and belongs to one opportunity. Shape it (days, rhythm, format), adapt it Unit by Unit (keep, modify, drop, add), or fill a gap with a new Unit — then confirm it with the client at Gate 2.
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px' }}>
          <div style={{ ...capsLabel, marginBottom: 4 }}>Programme</div>
          <Select value={slug} onChange={e => setSlug(e.target.value)}>
            {opts.map(o => <option key={o.slug} value={o.slug}>{o.name}{o.pending ? ' — composition pending' : ''}</option>)}
          </Select>
        </div>
        <Btn loading={busy} disabled={!slug} onClick={start}>Start the design →</Btn>
      </div>
      {children && <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)' }}>{children}</div>}
    </Card>
  );
}

function Slot({ s, d, locked, onAct, onNote, onMinutes, onRemove, tools }) {
  const bg = s.kind === 'add' ? '#f1f8f3' : s.kind === 'base' && s.act === 'modify' ? '#fff4dc' : 'transparent';
  const [note, setNote] = useState(s.note || '');
  const [min, setMin] = useState(s.minutes || '');
  useEffect(() => { setNote(s.note || ''); setMin(s.minutes || ''); }, [s.note, s.minutes]);
  const showNote = (s.kind === 'base' && s.act === 'modify') || s.kind !== 'base';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '70px minmax(0,1fr) auto', gap: 10, padding: '9px 14px', borderBottom: '1px solid #f3f3f3', alignItems: 'start', background: bg, borderLeft: s.kind === 'new' ? `4px solid ${GATE_RED}` : 'none' }}>
      <div style={{ fontSize: '0.76rem', color: 'var(--muted)', paddingTop: 2 }}>
        {s.kind === 'new' ? <b style={{ color: GATE_RED }}>NEW</b> : s.kind === 'add' ? <b style={{ color: '#1e6b34' }}>ADDED</b> : s.time}
        <br />
        {s.kind === 'base' ? (s.minutes ? `${s.minutes}′` : '') : (
          <span><input type="number" min="0" step="15" value={min} disabled={locked} title="minutes"
            onChange={e => setMin(e.target.value)} onBlur={() => String(min) !== String(s.minutes || '') && onMinutes(min)}
            style={{ width: 52, padding: '2px 4px', border: '1px solid var(--line)', borderRadius: 6, fontSize: '0.78rem' }} />′</span>
        )}
      </div>
      <div style={{ minWidth: 0, fontSize: '0.86rem' }}>
        <div style={s.kind === 'base' && s.act === 'drop' ? { textDecoration: 'line-through', color: '#9ca3af' } : null}>
          {s.kind === 'new' ? <b>{s.content}</b> : s.content}
          {s.kind === 'new' && s.from && <span style={unitChip}>from IDEA ({s.from})</span>}
        </div>
        {s.kind === 'new'
          ? <div style={tiny}>Draft Unit for this client → offered to the custodians ({FAMILIES[s.family] || 'family to choose'}).</div>
          : (s.units || []).map((c, i) => <span key={c} style={unitChip}>({c}) {s.unitNames?.[i] || ''}</span>)}
        {s.cards && s.kind === 'base' && <div style={tiny}>Memory cards: {s.cards}</div>}
        {showNote && (
          <div style={{ marginTop: 6 }}>
            <Input value={note} disabled={locked} onChange={e => setNote(e.target.value)} onBlur={() => note !== (s.note || '') && onNote(note)}
              placeholder={s.kind === 'new' ? 'What the new Unit covers for this client' : 'How it changes for this client — vocabulary, cases, examples'} />
          </div>
        )}
      </div>
      {!locked && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {s.kind === 'base'
            ? ACTS.map(([v, l]) => {
              const on = s.act === v;
              const style = on ? (v === 'keep' ? { background: 'var(--ink)', color: '#fff', borderColor: 'var(--ink)' } : v === 'modify' ? { background: '#e9b949', borderColor: '#e9b949' } : { background: '#9ca3af', color: '#fff', borderColor: '#9ca3af' }) : {};
              return <button key={v} onClick={() => !on && onAct(v)} style={{ ...actBtn, ...style }}>{l}</button>;
            })
            : <button onClick={onRemove} style={actBtn}>REMOVE</button>}
          {tools && <div style={{ flexBasis: '100%', display: 'flex', justifyContent: 'flex-end' }}>{tools}</div>}
        </div>
      )}
    </div>
  );
}
const unitChip = { display: 'inline-block', fontSize: '0.74rem', border: '1px solid #ddd', borderRadius: 6, padding: '0 6px', margin: '3px 4px 0 0', background: '#fff' };
const actBtn = { border: '1px solid #ddd', background: '#fff', borderRadius: 6, fontWeight: 700, fontSize: '0.7rem', padding: '3px 7px', cursor: 'pointer', fontFamily: 'var(--font-body)' };

function CatalogueModal({ data, startDay, onAdd, onGap, onClose }) {
  const [fam, setFam] = useState('');
  const [q, setQ] = useState('');
  const [day, setDay] = useState(startDay);
  const list = data.units.filter(u => (!fam || u.family === fam) && (!q || `${u.code} ${u.name}`.toLowerCase().includes(q.toLowerCase())));
  const sug = data.suggestions;
  const byCode = Object.fromEntries(data.units.map(u => [u.code, u]));
  return (
    <Modal open onClose={onClose} title="Units — the catalogue" width={720}>
      <p style={{ ...tiny, marginTop: 0 }}>{data.units.length} Units from Basecamp · a standard Unit is added as it is (say how it changes for the client); an IDEA Unit starts a new Unit.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        <label style={{ fontSize: '0.82rem' }}>Add to{' '}
          <Select value={day} onChange={e => setDay(Number(e.target.value))} style={{ width: 'auto', display: 'inline-block' }}>
            {data.design.on.map((_, i) => <option key={i} value={i + 1}>Day {i + 1}</option>)}
          </Select>
        </label>
        <Select value={fam} onChange={e => setFam(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All families</option>
          {Object.entries(FAMILIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search" style={{ flex: 1, minWidth: 120 }} />
      </div>
      {sug === null ? <p style={tiny}>Write the need in the Needs Summary to get suggestions.</p> : sug && sug.length > 0 && (
        <div style={{ border: '1px dashed var(--field-line)', background: 'var(--field)', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
          <b style={{ fontSize: '0.82rem' }}>✦ Suggested from the need</b> <span style={tiny}>(text suggestion — lighter than HB Compass facet matching)</span>
          {sug.map(s => {
            const u = byCode[s.code];
            return (
              <div key={s.code} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.82rem', marginTop: 6 }}>
                <div><b>({s.code})</b> {u?.name} {u?.idea && <span style={ideaTag}>idea</span>}
                  <div style={tiny}>matches: {s.hits.join(', ')}{s.inBase ? ' · already in the composition — consider MODIFY' : ''}</div></div>
                {u?.idea ? <Btn size="sm" variant="outline" onClick={() => onGap(day, s.code)}>Gap → new Unit</Btn>
                  : s.inBase ? null : <Btn size="sm" onClick={() => onAdd(day, s.code)}>Add</Btn>}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <tbody>
            {list.map(u => (
              <tr key={u.code} style={{ borderBottom: '1px solid var(--line)' }}>
                <td style={{ width: 62, padding: '6px 4px', verticalAlign: 'top' }}><b>{u.code}</b></td>
                <td style={{ padding: '6px 4px' }}>{u.name} {u.idea && <span style={ideaTag}>idea</span>}<div style={tiny}>{FAMILIES[u.family] || u.family}</div></td>
                <td style={{ width: '1%', padding: '6px 4px', whiteSpace: 'nowrap' }}>
                  {u.idea ? <Btn size="sm" variant="outline" onClick={() => onGap(day, u.code)}>Gap →</Btn> : <Btn size="sm" variant="outline" onClick={() => onAdd(day, u.code)}>Add</Btn>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
const ideaTag = { fontSize: '0.68rem', border: '1px solid var(--line)', borderRadius: 99, padding: '0 6px', color: 'var(--muted)' };

function GapModal({ data, day, base, onSave, onClose }) {
  const byCode = Object.fromEntries(data.units.map(u => [u.code, u]));
  const [title, setTitle] = useState(base ? byCode[base]?.name || '' : '');
  const [from, setFrom] = useState(base || '');
  const [family, setFamily] = useState(Object.keys(FAMILIES).find(k => (base || 'SE').startsWith(k)) || 'SE');
  const [minutes, setMinutes] = useState(60);
  return (
    <Modal open onClose={onClose} title={`Gap → new Unit · Day ${day}`}>
      <p style={{ ...tiny, marginTop: 0 }}>Name what the client needs and no Unit covers yet. The Unit is drafted for this client and offered to the custodians of its family.</p>
      <label style={fieldLbl}>Working title</label>
      <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Selling to a buying committee" />
      <label style={fieldLbl}>Starts from</label>
      <Select value={from} onChange={e => setFrom(e.target.value)}>
        <option value="">a blank draft</option>
        {data.units.filter(u => u.idea).map(u => <option key={u.code} value={u.code}>IDEA ({u.code}) {u.name}</option>)}
      </Select>
      <label style={fieldLbl}>Family</label>
      <Select value={family} onChange={e => setFamily(e.target.value)}>
        {Object.entries(FAMILIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </Select>
      <label style={fieldLbl}>Minutes</label>
      <Input type="number" min="0" step="15" value={minutes} onChange={e => setMinutes(e.target.value)} style={{ width: 120 }} />
      <div style={{ marginTop: 14 }}>
        <Btn onClick={() => onSave({ title, from, family, minutes })} style={{ background: GATE_RED, borderColor: GATE_RED }}>Add the gap</Btn>
      </div>
    </Modal>
  );
}
const fieldLbl = { display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--body-ink)', margin: '10px 0 4px' };

/**
 * The design editor. `gate2Wording` keeps the Compass / no-Compass Gate 2 sentence as built;
 * `after` renders under the Gate 2 box once confirmed (the "Continue to Proposal" link).
 */
export default function DesignEditor({ trackId, gate2Wording, after, onGateChanged, onEmpty, slotTools }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalogue, setCatalogue] = useState(null); // start day
  const [gap, setGap] = useState(null);             // { day, base }
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [resetArmed, setResetArmed] = useState(false);
  const wide = useWide();

  const load = useCallback(() => {
    api.master.getDesign(trackId).then(d => { setData(d); setError(null); }).catch(e => setError(e.message));
  }, [trackId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <Alert type="error">{error}</Alert>;
  if (!data) return <Card style={panel}><Loading /></Card>;
  if (!data.design) return onEmpty ? onEmpty(data, load) : <DesignStart trackId={trackId} data={data} onStarted={load} />;

  const D = data.design;
  const locked = data.gate2;
  const run = (p) => p.then(load).catch(e => alert(e.message));
  const shape = (patch) => run(api.master.shapeDesign(trackId, patch));
  const addUnit = (day, code) => run(api.master.addDesignSlot(trackId, { day, kind: 'add', unitCode: code }));
  const addGap = (day, g) => run(api.master.addDesignSlot(trackId, { day, kind: 'new', title: g.title, from: g.from || null, family: g.family, minutes: g.minutes }));

  async function gate() {
    setBusy(true);
    try {
      if (locked) await api.master.reverseGate(trackId, 2);
      else await api.master.confirmGate(trackId, 2);
      load(); onGateChanged && onGateChanged();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function reset() {
    if (!resetArmed) { setResetArmed(true); return; }
    setResetArmed(false);
    run(api.master.resetDesign(trackId));
  }

  return (
    <>
      <Card style={panel}>
        <div style={tiny}>{data.client}</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', margin: '4px 0' }}>Design — {D.programme} for {data.client}</h2>
        <div style={tiny}>Starting composition: {D.base}{D.hasComp ? ' · Basecamp' : ''}</div>
        <div style={{ marginTop: 14 }}>
          <div style={capsLabel}>Customisation level — follows from what you change</div>
          <Ladder level={D.level} />
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: wide ? 'minmax(0,1fr) 330px' : 'minmax(0,1fr)', gap: 14, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          <Card style={panel}>
            <h3 style={h2}>1 · Shape</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
              <label style={{ flex: '1 1 200px', fontSize: '0.82rem' }}>Rhythm
                <Select value={D.rhythm} disabled={locked} onChange={e => shape({ rhythm: e.target.value })}>{RHYTHMS.map(x => <option key={x}>{x}</option>)}</Select>
              </label>
              <label style={{ flex: '1 1 200px', fontSize: '0.82rem' }}>Format
                <Select value={D.format} disabled={locked} onChange={e => shape({ format: e.target.value })}>{FORMATS.map(x => <option key={x}>{x}</option>)}</Select>
              </label>
            </div>
            <p style={{ ...tiny, margin: '8px 0 0' }}>Switch a day off in its header. {D.hasComp ? '' : 'This programme has no composition in Basecamp yet — compose it from Units: + Unit in each day.'}</p>
          </Card>

          <Card style={panel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={h2}>2 · Adapt — the day grid</h3>
              {!locked && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn size="sm" variant="outline" onClick={() => run(api.master.addDesignDay(trackId))}>+ Day</Btn>
                  <Btn size="sm" onClick={() => setCatalogue(1)}>+ Unit from the catalogue</Btn>
                </div>
              )}
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: '6px 0 12px' }}>
              For each slot: <b>KEEP</b> as composed, <b>MODIFY</b> for this client (say how), or <b>DROP</b>. Add Units from the catalogue, or name a gap that needs a new Unit.
            </p>
            {D.days.map((day, i) => {
              const on = D.on[i];
              const mins = day.filter(s => s.kind !== 'base' || s.act !== 'drop').reduce((a, s) => a + (Number(s.minutes) || 0), 0);
              return (
                <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 12, marginBottom: 12, overflow: 'hidden', background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 14px', background: '#fafafa', borderBottom: '1px solid var(--line)', gap: 8, flexWrap: 'wrap', opacity: on ? 1 : 0.55 }}>
                    <b style={{ fontFamily: 'var(--font-display)' }}>Day {i + 1}</b>
                    <span style={tiny}>{mins} min</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {!locked && <Btn size="sm" variant="outline" onClick={() => setGap({ day: i + 1, base: null })}>+ Gap → new Unit</Btn>}
                      {!locked && <Btn size="sm" variant="outline" onClick={() => setCatalogue(i + 1)}>+ Unit</Btn>}
                      <label style={{ fontSize: '0.82rem' }}>
                        <input type="checkbox" checked={on} disabled={locked} onChange={e => shape({ on: D.on.map((x, j) => (j === i ? e.target.checked : x)) })} /> in the programme
                      </label>
                    </div>
                  </div>
                  {!on ? <div style={{ ...tiny, padding: '9px 14px' }}>Day left out of this programme.</div>
                    : day.length === 0 ? <div style={{ ...tiny, padding: '9px 14px' }}>No Units yet — + Unit.</div>
                      : day.map(s => (
                        <Slot key={s.id} s={s} d={i} locked={locked} tools={slotTools ? slotTools(s) : null}
                          onAct={act => run(api.master.updateDesignSlot(trackId, s.id, { act }))}
                          onNote={note => run(api.master.updateDesignSlot(trackId, s.id, { note }))}
                          onMinutes={minutes => run(api.master.updateDesignSlot(trackId, s.id, { minutes }))}
                          onRemove={() => run(api.master.removeDesignSlot(trackId, s.id))} />
                      ))}
                </div>
              );
            })}
          </Card>
        </div>

        <div style={{ position: wide ? 'sticky' : 'static', top: 12, minWidth: 0 }}>
          <Card style={panel}>
            <h3 style={h2}>Pair with Measure</h3>
            <Select value={D.measure} disabled={locked} onChange={e => shape({ measure: e.target.value })} style={{ marginTop: 8 }}>
              {MEASURES.map(x => <option key={x}>{x}</option>)}
            </Select>
            <p style={{ ...tiny, margin: '6px 0 0' }}>{/Compass/.test(D.measure)
              ? 'With HB Compass the profile ranks the Units by facet match; here the suggestions read the need text.'
              : 'Suggestions read the need text in the Needs Summary — a lighter suggestion.'}</p>
            <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '14px 0' }} />
            <div style={{ display: 'flex', gap: 18 }}>
              {[[D.daysOn, 'days'], [D.groups, 'group(s)'], [Number(D.value || 0).toLocaleString('en-GB'), 'EUR']].map(([n, l]) => (
                <div key={l} style={tiny}><b style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--ink)' }}>{n}</b>{l}</div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <label style={{ flex: 1, fontSize: '0.82rem' }}>Groups
                <NumberField value={D.groups} min={1} disabled={locked} onCommit={v => shape({ groups: v })} />
              </label>
              <label style={{ flex: 1, fontSize: '0.82rem' }}>Day price (EUR)
                <NumberField value={D.price} min={0} step={100} disabled={locked} onCommit={v => shape({ price: v })} />
              </label>
            </div>
            <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '14px 0' }} />
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4 }}>What changes for the client ({D.changes.length})</div>
            {D.changes.length ? (
              <ul style={{ fontSize: '0.82rem', paddingLeft: 16, margin: '4px 0' }}>
                {D.changes.map((c, i) => <li key={i}><b>{c[0]}:</b> {c[1]}</li>)}
              </ul>
            ) : <p style={{ ...tiny, margin: 0 }}>The standard composition, as it is.</p>}
            <Btn variant="outline" onClick={() => setOutlineOpen(true)} style={{ width: '100%', marginTop: 8 }}>Programme outline for the proposal</Btn>

            <div style={{ border: `2px solid ${GATE_RED}`, borderRadius: 12, padding: 12, marginTop: 12 }}>
              <div style={{ color: GATE_RED, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.06em' }}>GATE 2 · DESIGN CONFIRMED</div>
              <p style={{ fontSize: '0.82rem', margin: '4px 0 8px' }}>
                {locked
                  ? `Confirmed${D.confirmedAt ? ` on ${new Date(D.confirmedAt).toLocaleDateString('en-GB')}` : ''}. Groups, days and day price are in the project's commercials.`
                  : 'When the client approves the design: groups, days and day price go to the commercials and the Gate 2 step is confirmed.'}
              </p>
              {!locked && gate2Wording && <p style={{ ...tiny, margin: '0 0 8px' }}>{gate2Wording}</p>}
              {locked
                ? <Btn variant="outline" loading={busy} onClick={gate}>Reopen the design</Btn>
                : <Btn loading={busy} onClick={gate} style={{ background: GATE_RED, borderColor: GATE_RED }}>Confirm design</Btn>}
              {locked && after && <div style={{ marginTop: 10 }}>{after}</div>}
            </div>
            {!locked && D.isDesign && (
              <Btn variant="outline" onClick={reset} style={{ width: '100%', marginTop: 10 }}>
                {resetArmed ? 'Click again — every change is removed' : 'Back to the standard composition'}
              </Btn>
            )}
          </Card>
        </div>
      </div>

      {catalogue && (
        <CatalogueModal data={data} startDay={catalogue} onClose={() => setCatalogue(null)}
          onAdd={(day, code) => addUnit(day, code)}
          onGap={(day, code) => { setCatalogue(null); setGap({ day, base: code }); }} />
      )}
      {gap && (
        <GapModal data={data} day={gap.day} base={gap.base} onClose={() => setGap(null)}
          onSave={g => { setGap(null); addGap(gap.day, g); }} />
      )}
      {outlineOpen && (
        <Modal open onClose={() => setOutlineOpen(false)} title="Programme outline">
          <p style={{ ...tiny, marginTop: 0 }}>For the proposal: the programme as the client will see it.</p>
          <textarea readOnly value={D.outline} style={{ width: '100%', height: 320, fontFamily: 'var(--font-body)', fontSize: '0.85rem', border: '1px solid var(--line)', borderRadius: 8, padding: 10 }} />
          <Btn style={{ marginTop: 8 }} onClick={() => navigator.clipboard?.writeText(D.outline).catch(() => {})}>Copy</Btn>
        </Modal>
      )}
    </>
  );
}

function NumberField({ value, onCommit, ...rest }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  return <Input type="number" value={v} onChange={e => setV(e.target.value)} onBlur={() => String(v) !== String(value ?? '') && onCommit(v)} {...rest} />;
}
